/** Emissão de receituário com várias competências mensais na mesma receita (impressão em páginas separadas). */

const MAX_MULTI_MONTH_COUNT = 12;
const MAX_MULTI_MONTH_STEP = 6;

export function getMultiMonthCount(printSettings: unknown): number {
  const raw = (printSettings as { multi_month_count?: unknown } | null | undefined)?.multi_month_count;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 2) return 1;
  return Math.min(Math.floor(n), MAX_MULTI_MONTH_COUNT);
}

/** Meses entre uma folha e a próxima (uma embalagem de 60 cp a 30/mês cobre 2 meses). */
export function getMultiMonthStep(printSettings: unknown): number {
  const raw = (printSettings as { multi_month_step?: unknown } | null | undefined)?.multi_month_step;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 2) return 1;
  return Math.min(Math.floor(n), MAX_MULTI_MONTH_STEP);
}

/** Soma meses mantendo o dia; se o mês destino for mais curto, usa o último dia (31/01 + 1 → 28/02). */
export function addMonthsToIsoDate(isoDate: string | null | undefined, months: number): string {
  if (!isoDate) return '';
  const m = String(isoDate).split('T')[0].match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const totalMonths = Number(m[1]) * 12 + (Number(m[2]) - 1) + Math.trunc(months);
  const year = Math.floor(totalMonths / 12);
  const monthIndex = totalMonths - year * 12;
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const day = Math.min(Number(m[3]), lastDay);
  return `${String(year).padStart(4, '0')}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function isoToUtcMs(iso: string): number | null {
  const m = String(iso || '').split('T')[0].match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

function addDaysToIsoDate(iso: string, days: number): string {
  const ms = isoToUtcMs(iso);
  if (ms == null) return '';
  return new Date(ms + days * 86400000).toISOString().split('T')[0];
}

/** Avança a emissão `monthOffset` meses; a validade mantém a mesma duração em dias da original. */
export function shiftPrescriptionDates(
  issueDate: string | null | undefined,
  validityDate: string | null | undefined,
  monthOffset: number,
): { issueDate: string; validityDate: string } {
  if (monthOffset <= 0) {
    return {
      issueDate: String(issueDate || ''),
      validityDate: String(validityDate || ''),
    };
  }
  const shiftedIssue = addMonthsToIsoDate(issueDate, monthOffset);
  const issueMs = isoToUtcMs(String(issueDate || ''));
  const validityMs = isoToUtcMs(String(validityDate || ''));
  const shiftedValidity = issueMs != null && validityMs != null && shiftedIssue
    ? addDaysToIsoDate(shiftedIssue, Math.round((validityMs - issueMs) / 86400000))
    : addMonthsToIsoDate(validityDate, monthOffset);
  return { issueDate: shiftedIssue, validityDate: shiftedValidity };
}

export function formatMonthCompetence(isoDate: string): string {
  if (!isoDate) return '-';
  const d = new Date(String(isoDate).split('T')[0] + 'T12:00:00');
  const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** `monthStep` omitido preserva o intervalo já salvo. */
export function buildMultiMonthPrintSettings(
  existing: Record<string, unknown> | null | undefined,
  monthCount: number,
  monthStep?: number,
): Record<string, unknown> {
  const next = { ...(existing || {}) };
  if (monthCount > 1) {
    next.multi_month_count = monthCount;
    const step = monthStep ?? getMultiMonthStep(existing);
    if (step > 1) next.multi_month_step = Math.min(Math.floor(step), MAX_MULTI_MONTH_STEP);
    else delete next.multi_month_step;
  } else {
    delete next.multi_month_count;
    delete next.multi_month_step;
  }
  return next;
}
